import { UploadedFile } from "@/Contexts/Funnel/types";
import useDirectUpload from "@/hooks/useDirectUpload";
import FilesList from "@/shared-components/FilesList";
import { objectLength } from "@/utils/helperFunctions";
import DownloadIcon from "@mui/icons-material/Download";
import { Box, Button, Stack, Typography } from "@mui/material";
import { filter } from "lodash";

interface BulkImportSectionProps {
  importType: "human" | "equipment" | "clients";
  files: UploadedFile[];
  setFiles: React.Dispatch<React.SetStateAction<UploadedFile[]>>;
}

const BulkImportSection: React.FC<BulkImportSectionProps> = ({ importType, files, setFiles }) => {
  const { uploadFile } = useDirectUpload();

  const sampleFileName = importType === "human"
    ? "Human Resource Sample.xlsx"
    : importType === "equipment"
      ? "Equipment Resource Sample.xlsx"
      : "Client Sample.xlsx";

  const onFileRemove = (fileId: string) => {
    setFiles((previous) => filter(previous, file => file.id !== fileId));
  };

  const onFileUpload = async (uploadedFiles: File[]) => {
    const placeholders = uploadedFiles.map((file, index) => ({
      id: `file-${Date.now()}-${index}`,
      name: file.name,
      size: file.size,
      type: file.type,
      uploadStatus: "pending" as const,
    }));

    setFiles((previous) => [...previous, ...placeholders]);

    const completedFiles = await Promise.all(uploadedFiles.map(async (file, index) => {
      const placeholder = placeholders[index];
      setFiles((previous) => previous.map(item =>
        item.id === placeholder.id ? { ...item, uploadStatus: "uploading" } : item
      ));

      const result = await uploadFile(file, `bulk-import-${importType}`).catch(() => null);
      return result
        ? { ...placeholder, uploadStatus: "completed" as const, signedBlobId: result.signedBlobId, previewUrl: result.previewUrl }
        : { ...placeholder, uploadStatus: "error" as const, error: "Upload failed" };
    }));

    setFiles((previous) => previous.map(file =>
      completedFiles.find(completed => completed.id === file.id) || file
    ));
  };

  return (
    <Box sx={{ height: "100%", pl: { md: 3 } }}>
      <Stack spacing={0.75} mb={2.5}>
        <Typography variant="h6" fontWeight={600}>Or import in bulk</Typography>
        <Typography variant="body2" color="text.secondary">
          Upload a completed spreadsheet to create several resources at once.
        </Typography>
      </Stack>

      <Stack spacing={1.5}>
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>Start with the sample file</Typography>
          <Typography variant="caption" color="text.secondary">
            Note: keep its column headings unchanged to help prevent invalid data.
          </Typography>
        </Box>

        <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadIcon />}
          component="a"
          href={`/samples/${encodeURIComponent(sampleFileName)}`}
          download={sampleFileName}
          sx={{ alignSelf: "flex-start" }}
        >
          Download sample spreadsheet
        </Button>

        <FilesList files={files} onFileRemove={onFileRemove} onFileUpload={onFileUpload} />
        {objectLength(files) > 1 && (
          <Typography variant="body2" color="error.main" fontWeight={500}>
            Only one file can be imported at a time. Please remove the additional files.
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

export default BulkImportSection;
